import { useCallback } from 'react'
import axios from 'axios'

import { INSURANCE_UNIQUE_ID, MODALITY_RETURN_PREFIX } from '../../constants'

const useQuotationClient = () => {
  const registerBail = useCallback(async (quotation) => axios.post('/quote/bail', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerGuaranteeBID = useCallback(async (quotation) => axios.post('/quote/bid', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerGuaranteePerformance = useCallback(async (quotation) => axios.post('/quote/performance', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerJudicialGuaranteeAppeal = useCallback(async (quotation) => axios.post('/quote/judicial', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerJudicialGuaranteeCivel = useCallback(async (quotation) => axios.post('quote/judicialcivel', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerJudicialGuaranteeLabor = useCallback(async (quotation) => axios.post('/judicial-labor', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerEngineeringRisk = useCallback(async (quotation) => axios.post('/quote/engineeringrisk', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerGuaranteeOther = useCallback(async (quotation) => axios.post('/quote/guaranteeOther', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const sendInfoPayment = useCallback(async (quotation) => axios.post('/quote/payment', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const sendAnalysisRequest = useCallback(async (analysisRequest) => axios.post('/quote/analysis', { request: { ...analysisRequest } }).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getQuotationDetails = useCallback(async (quotationIdentifier) => axios.get(`/quote?identifier=${quotationIdentifier}`).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getQuotationManualDetails = useCallback(async (quotationIdentifier) => axios.get(`/quote/manual?identifier=${quotationIdentifier}`).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getStatusQuotationSimplified = useCallback(async () => axios.get('/statusCotacaoSimplificado').then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const edit = useCallback(async (quotation) => axios.put('/quote/edit', { request: { ...quotation } }).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const deleteQuote = useCallback(async (quotationIdentifier, clientEmail) => axios.delete(`/quote/delete?identifier=${quotationIdentifier}&clientEmail=${clientEmail}`).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const sendDocumentsWings = useCallback(async (data) => axios.post('/document/save', data, {
    noLoading: true,
  }).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const downloadDocumentsWings = useCallback(async (document) => axios.get(`/wings/document?wingsDocumentId=${document}&base64=true`), [])

  const getPaymentType = useCallback(async (document) => axios.get(`/client/paymentType?cpfCnpj=${document}`), [])

  const getDraftQuote = useCallback(async (data) => axios.get('/quote/draft/document', {
    params: { ...data },
  }).then((response) => (response?.data ? response : { data: { ...response } })), [])

  const getQuoteReceiptViewer = useCallback(async (documentNumber) => axios.get(`/quotes/export/pdf/${documentNumber}?base64=true`)
    .then((response) => (response?.data ? response : { data: { ...response } })), [])

  const getQuoteEndorsementReceiptViewer = useCallback(async (documentNumber) => axios.get(`/InsurancePolicy/endorsement/pdf/${documentNumber}?base64=true`)
    .then((response) => (response?.data ? response : { data: { ...response } })), [])

  const getInfosLimitAndRate = useCallback(async (document) => axios.get(`/client/creditAnalysis/${document}`)
    .then((response) => (response?.data ? response : { data: { ...response } })), [])

  const getListLimitAndRate = useCallback(async () => axios.get('/client/myClients')
    .then((response) => (response?.data ? response : { data: { ...response } })), [])

  const sendEndorsementAutomatic = useCallback(async (data) => axios.post('/endorsement-automatic', data).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const sendRERequestPerformance = useCallback(async (data) => axios.post('/opportunity/engineeringRisks', data).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const uploadAndExtractFile = useCallback(async (productKey, wingsDocumentId, flowType) => axios.post('/ai/upload-and-extract-file', null, {
    params: {
      productKey,
      wingsDocumentId,
      flowType,
    },
  }).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getSubmittedDocument = useCallback(async (submittedDocumentId) => axios.get(`/ai/submitted-document/${submittedDocumentId}?includes=ExternalSources`).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const submitToQuote = useCallback(async (submittedDocumentId, data) => axios.post(`/ai/submitted-document/${submittedDocumentId}/submit-to-quote`,
    data).then((response) => (response?.data ? response : { data: { ...response } })), [])

  // MULTICALCULO
  const getInsuranceCompaniesByProduct = useCallback((clientDocument) => (
    axios.get('/client/user/insurance-company-by-product', {
      params: { clientDocument },
      noLoading: true,
    }).then((response) => response.data)
  ), [])

  const getInsuranceProductsMulticalculo = useCallback((clientDocument) => (
    axios.get('/client/user/products-multi-quote', {
      params: { clientDocument },
      noLoading: true,
    }).then((response) => response.data)
  ), [])

  const isModalityMultiQuote = useCallback((modalityPath, insuranceProducts) => {
    const productCode = MODALITY_RETURN_PREFIX[modalityPath]

    if (!productCode) return false

    return !!insuranceProducts?.find((item) => item.Code === productCode)?.HasMultiQuote
  }, [])

  const getQuotationDetailsMulticalculo = useCallback(async (quotationIdentifier) => axios.get(`/MultiQuote?identifier=${quotationIdentifier}`).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getStatusQuotationMulticalculo = useCallback(async (quotationIdentifier) => axios.get('/MultiQuoteStatus', {
    params: { identificador: quotationIdentifier },
    noLoading: true,
  }).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const sendAnalysisRequestMulticalculo = useCallback(
    async (identifier, reasonAnalysis, insuranceCompanyId) => axios
      .post('/MultiQuoteAnalisys', {
        request: {
          Identifier: identifier,
          ReasonAnalysis: reasonAnalysis,
          InsuranceCompanyId: insuranceCompanyId,
        },
      })
      .then((response) => (response?.data ? response : { data: { ...response } })),
    [],
  )

  const sendInfoPaymentMulticalculo = useCallback(
    async (identifier, proposalUniqueId, paymentDate, installmentNumber) => axios
      .get('/MultiQuotePolicyRequest', {
        params: {
          Identificador: identifier,
          ProposalUniqueId: proposalUniqueId,
          DataPagamentoParcela: paymentDate,
          NumeroParcela: installmentNumber,
        },
      })
      .then((response) => response.data),
    [],
  )

  const getDraftMulticalculo = useCallback(async ({
    identifier,
    proposalUniqueId,
    insuranceUniqueId,
    documentId,
  }) => {
    if (insuranceUniqueId === INSURANCE_UNIQUE_ID.POTTENCIAL) {
      return axios.get('/quote/draft/document', {
        params: { base64: true, codigo: identifier },
      })
    }

    if (insuranceUniqueId === INSURANCE_UNIQUE_ID.AVLA) {
      return axios.get('/Avla/DownloadDocument', {
        params: { documentId, InsuranceUniqueId: insuranceUniqueId, base64: true },
      })
    }

    return axios.get('/OnPoint/DownloadDocument', {
      params: {
        ProposalUniqueId: proposalUniqueId,
        InsuranceUniqueId: insuranceUniqueId,
        DocumentType: 'Draft',
        base64: true,
      },
    })
  }, [])

  const registerGuaranteeBIDMulticalculo = useCallback(async (quotation) => axios.post('/MultiQuote/BID', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerGuaranteePerformanceMulticalculo = useCallback(async (quotation) => axios.post('/MultiQuote/Performance', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const registerJudicialGuaranteeAppealMulticalculo = useCallback(async (quotation) => axios.post('/MultiQuote/JDR', quotation).then((response) => (
    response?.data ? response : { data: { ...response } })), [])

  const getQuoteReceiptViewerMulticalculo = useCallback(async (documentNumber) => axios.get(`/quotes/export/pdf/multiquote/${documentNumber}?base64=true`), [])

  return useCallback(() => ({
    edit,
    deleteQuote,
    registerBail,
    getDraftQuote,
    getPaymentType,
    sendInfoPayment,
    sendAnalysisRequest,
    getQuotationDetails,
    getQuoteReceiptViewer,
    getQuoteReceiptViewerMulticalculo,
    sendRERequestPerformance,
    getQuoteEndorsementReceiptViewer,
    registerGuaranteeBID,
    registerGuaranteeOther,
    registerEngineeringRisk,
    getQuotationManualDetails,
    getStatusQuotationSimplified,
    registerGuaranteePerformance,
    registerJudicialGuaranteeCivel,
    registerJudicialGuaranteeLabor,
    registerJudicialGuaranteeAppeal,
    sendDocumentsWings,
    downloadDocumentsWings,
    getInfosLimitAndRate,
    getListLimitAndRate,
    sendEndorsementAutomatic,
    uploadAndExtractFile,
    getSubmittedDocument,
    submitToQuote,
    sendAnalysisRequestMulticalculo,
    sendInfoPaymentMulticalculo,
    getDraftMulticalculo,
    registerGuaranteeBIDMulticalculo,
    registerGuaranteePerformanceMulticalculo,
    registerJudicialGuaranteeAppealMulticalculo,
    getQuotationDetailsMulticalculo,
    getStatusQuotationMulticalculo,
    getInsuranceCompaniesByProduct,
    getInsuranceProductsMulticalculo,
    isModalityMultiQuote,
  }), [
    edit,
    deleteQuote,
    registerBail,
    getDraftQuote,
    getPaymentType,
    sendInfoPayment,
    sendAnalysisRequest,
    getQuotationDetails,
    getQuoteReceiptViewer,
    getQuoteReceiptViewerMulticalculo,
    sendRERequestPerformance,
    getQuoteEndorsementReceiptViewer,
    registerGuaranteeBID,
    registerGuaranteeOther,
    registerEngineeringRisk,
    getQuotationManualDetails,
    getStatusQuotationSimplified,
    registerGuaranteePerformance,
    registerJudicialGuaranteeCivel,
    registerJudicialGuaranteeLabor,
    registerJudicialGuaranteeAppeal,
    sendDocumentsWings,
    downloadDocumentsWings,
    getInfosLimitAndRate,
    getListLimitAndRate,
    sendEndorsementAutomatic,
    uploadAndExtractFile,
    getSubmittedDocument,
    submitToQuote,
    sendAnalysisRequestMulticalculo,
    sendInfoPaymentMulticalculo,
    getDraftMulticalculo,
    registerGuaranteeBIDMulticalculo,
    registerGuaranteePerformanceMulticalculo,
    registerJudicialGuaranteeAppealMulticalculo,
    getQuotationDetailsMulticalculo,
    getStatusQuotationMulticalculo,
    getInsuranceCompaniesByProduct,
    getInsuranceProductsMulticalculo,
    isModalityMultiQuote,
  ])
}

export default useQuotationClient
